import apiCall from '@/utils/apiCall';
import getActiveSubscription from '@/utils/getActiveSubscription';
import { canUpgrade } from '@/utils/plans';
import {
    calculateAnnualAmount,
    calculateMonthlyAmount,
} from '@/utils/price';
import { mapValues } from 'lodash';
import moment from 'moment';

export default {
    /**
     * Sets user card
     *
     * @param   {object}   params        Params.
     * @param   {Function} params.commit Commit mutation.
     * @param    {object}   params.state   Vuex state.
     *
     */
    async loadUserCard(
        {
            commit,
            dispatch,
            state,
        },
        {
            withoutSubscription = false,
        } = {}
    ) {
        commit('setUserCard', null);

        if (! state.paymentMethods && ! withoutSubscription) {
            dispatch('loadPaymentMethods');
            return;
        }

        if (! state.userSubscription && ! withoutSubscription) {
            return;
        }

        if (withoutSubscription) {
            const { status, data } = await apiCall(this._vm, 'get', '/api/billing/payments/methods/default');

            if (
                200 !== status ||
                data.error ||
                (moment().year() > data.exp_year &&
                    moment().month() > data.exp_month - 1)
            ) {
                commit('setUserCard', false);
                return;
            }

            commit('setUserCard', data);
            return;
        }

        const { default_payment_method } = state.userSubscription;

        for (const card of state.paymentMethods) {
            if (card.id === default_payment_method) {
                commit('setUserCard', card);
                return;
            }
        }

        commit('setUserCard', false);
    },

    /**
     * Load payment methods.
     *
     * @param {object}   params         Params.
     * @param {Function} params.commit  Commit mutation.
     * @param {object}   params.state   Vuex state.
     */
    async loadPaymentMethods({
        commit,
        dispatch,
        state,
    }) {
        if (state.paymentMethods) {
            return;
        }

        const { status, data } = await apiCall(this._vm, 'get', '/api/billing/payments/methods');

        if (200 === status) {
            commit('setPaymentMethods', data);
            dispatch('loadUserCard');
        }
    },

    /**
     * Reloads payment methods.
     *
     * @param {object}   params         Params.
     * @param {Function} params.commit  Commit mutation.
     * @param {object}   params.state   Vuex state.
     */
    async reloadPaymentMethods({ commit, dispatch }) {
        commit('setPaymentMethods', null);
        dispatch('loadUserSubscription', { force: true });
    },

    /**
     * Sets user subscriptions
     *
     * @param {object}   params        Params.
     * @param {Function} params.commit Commit mutation.
     * @param {object}   params.state  Vuex state.
     * @param {number}   id            Subscription ID (optional).
     */
    async loadUserSubscription(
        {
            commit,
            state,
            dispatch,
        },
        {
            force = false,
        } = {}
    ) {
        const { subscriptions } = state.user;

        if (
            0 === subscriptions.length ||
            (
                true !== force &&
                state.currentSubscription
            )
        ) {
            return;
        }

        const subscription = getActiveSubscription(subscriptions);

        if (undefined === subscription) {
            return;
        }

        commit('setCurrentSubscription', subscription);
        commit('setUserSubscription', null);
        commit('setUserCard', null);
        commit('setInvoices', null);

        const { status, data } = await apiCall(this._vm, 'get', `/api/billing/subscriptions/${subscription.id}`);

        if (200 !== status) {
            return;
        }

        data.dbId = subscription.id;
        data.name = subscription.name;
        data.interval = null;

        for (const item of data?.items?.data ?? []) {
            data.interval = item?.price?.recurring?.interval ?? null;

            break;
        }

        try {
            const {
                status: paymentMethodStatus,
                data: paymentMethodData,
            } = await apiCall(this._vm, 'get', '/api/billing/payments/methods/default');

            if (200 === paymentMethodStatus && paymentMethodData.id) {
                data.default_payment_method = paymentMethodData.id;
            }
        } catch (e) {} // eslint-disable-line no-empty

        commit('setUserSubscription', data);
        dispatch('loadUserCard');
        dispatch('loadInvoices');
    },

    /**
     * Load invoices.
     *
     * @param {object}   params         Params.
     * @param {Function} params.commit  Commit mutation.
     * @param {object}   params.state   Vuex state.
     */
    async loadInvoices({
        commit,
        state,
    }, { force = false } = {}) {
        if (! state.currentSubscription) {
            return;
        }

        if (null === state.invoices || true === force) {
            commit('setInvoices', null);
            const { status, data } = await apiCall(this._vm, 'get', `/api/billing/subscriptions/${state.currentSubscription.stripe_id}/invoices`);

            const invoices = 200 === status ? data : false;
            commit('setInvoices', invoices);
        }
    },

    /**
     * Loads upcoming user invoice
     *
     * @param {object}   params        Params.
     * @param {Function} params.commit Commit mutation.
     * @param {object}   params.state  Vuex state.
     * @param {object}   planData      Plan data to evaluate incoming invoice.
     */
    async loadUpcomingInvoice({
        commit,
        state,
    }, {
        interval = null,
        presumed = false,
    } = {}) {
        const key = presumed ? 'setPresumedInvoice' : 'setUpcomingInvoice';

        commit(key, null);

        if (! state.currentSubscription) {
            return;
        }

        const subscriptionId = state.currentSubscription.id;
        const route = `/api/billing/subscriptions/${subscriptionId}/invoices/preview`;

        try {
            const response = await apiCall(this._vm, 'get', route, {
                params: { interval, invoice: (presumed ? 1 : null) },
            });

            if (200 === response.status) {
                commit(key, response.data);
            } else {
                commit(key, false);
            }
        } catch (e) {
            commit(key, false);
        }
    },

    /**
     * Load profile controls
     *
     * @param  {object}    store        Vuex store.
     * @param  {object}    store.state  Vuex state.
     * @param  {Function}  store.commit Method to commit a mutation.
     * @returns {Promise}               Promise.
     */
    async loadProfileControls({ state, commit }) {
        if ('object' === typeof state.profileControls && null !== state.profileControls) {
            return;
        }

        let profileControls = false;

        const { status, data } = await apiCall(this._vm, 'get', '/api/user/profile-controls/onboarding');

        if (200 === status && data) {
            profileControls = data;
        }

        commit('setProfileControls', profileControls);
    },

    /**
     * Gets billing plans
     *
     * @param  {object}    store        Vuex store.
     * @param  {object}    store.state  Vuex state.
     * @param  {Function}  store.commit Method to commit a mutation.
     * @returns {Promise}               Promise.
     */
    async getBillingPlans(
        { state, commit },
        { coupon, force = false } = {}
    ) {
        if (! force && state.billingPlans) {
            return;
        }

        const { status, data } = await apiCall(this._vm, 'get', '/api/billing/plans');

        if (200 !== status) {
            throw data.message;
        }

        const plans = data

            /**
             * Sort plans in upgrade order. The first plan will be the lowest with
             * no downgrade option, the last - the highest with no upgrade option.
             */
            .sort((a, b) => {
                return canUpgrade(a, b)
                    ? -1
                    : canUpgrade(b, a)
                        ? 1 : 0;
            })

            /**
             * Add price calculations
             */
            .map((plan) => ({
                ...plan,
                prices: plan.prices ? mapValues(plan.prices, (price) => ({
                    ...price,
                    monthlyAmount: calculateMonthlyAmount(price),
                    annualAmount: calculateAnnualAmount(price),
                })) : plan.prices,
            }));

        commit('setBillingPlans', plans);
    },

    /**
     * Loads plan features.
     *
     * @param  {object}    store        Vuex store.
     * @param  {object}    store.state  Vuex state.
     * @param  {Function}  store.commit Method to commit a mutation.
     * @returns {Promise}               Promise.
     */
    async loadPlanFeatures(
        { state, commit },
        { force = false } = {}
    ) {
        if (! force && state.planFeatures) {
            return;
        }

        const { status, data } = await apiCall(this._vm, 'get', '/api/billing/plan-features');

        if (200 !== status) {
            throw data.message;
        }

        commit('setPlanFeatures', data);
    },

    /**
     * Gets billing summary.
     *
     * @param  {object}    store        Vuex store.
     * @param  {object}    store.state  Vuex state.
     * @param  {Function}  store.commit Method to commit a mutation.
     * @returns {Promise}               Promise.
     */
    async getBillingSummary(
        { state, commit, dispatch },
        { force = false } = {}
    ) {
        if (! force && state.billingSummary) {
            return;
        }

        await dispatch('loadUserSubscription');
        const subscription = state.currentSubscription;

        if (! subscription) {
            return;
        }

        const { status, data } = await apiCall(this._vm, 'get', `/api/billing/subscriptions/${subscription.id}/summary`);

        if (200 !== status) {
            throw data.message;
        }

        commit('setBillingSummary', data);
    },
};
